import React, { useMemo } from 'react';

import { css, cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import { SmartLinkIconRenderer } from './SmartLinkIconRenderer.tsx';
import {
	type BylineConnectionIcon,
	getBylineConnectionIcons,
} from './utils/getBylineConnectionIcons.ts';
import type { EnhancedSmartLink } from './utils/smartLinkTypes.ts';

const styles = cssMap({
	stack: {
		display: 'inline-flex',
		alignItems: 'center',
		flexShrink: 0,
		marginInlineEnd: token('space.050'),
		isolation: 'isolate',
	},
	reservedStack: {
		// Three 16px tiles with two 4px overlaps occupy 40px.
		minWidth: '40px',
		justifyContent: 'flex-end',
	},
});

const iconTileStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	boxSizing: 'border-box',
	position: 'relative',
	flexShrink: 0,
	width: '16px',
	height: '16px',
	backgroundColor: token('elevation.surface'),
	borderColor: token('color.border'),
	borderStyle: 'solid',
	borderWidth: token('border.width'),
	borderRadius: token('radius.small'),
});

const placeholderIconTileStyles = css({
	backgroundColor: token('color.background.disabled'),
	borderColor: token('color.border.disabled'),
});

const firstIconTileStyles = css({
	zIndex: 300,
});

const secondIconTileStyles = css({
	marginInlineStart: token('space.negative.050'),
	zIndex: 200,
	rotate: '6deg',
});

const thirdIconTileStyles = css({
	marginInlineStart: token('space.negative.050'),
	zIndex: 100,
});

export type BylineConnectionIconStackProps = {
	links: EnhancedSmartLink[];
	additionalIcons?: BylineConnectionIcon[];
	appearance?: 'default' | 'placeholder';
	reserveMaximumWidth?: boolean;
};

export const BylineConnectionIconStack = ({
	links,
	additionalIcons,
	appearance = 'default',
	reserveMaximumWidth = false,
}: BylineConnectionIconStackProps): React.JSX.Element | null => {
	const icons = useMemo(
		() => getBylineConnectionIcons(links, additionalIcons),
		[links, additionalIcons],
	);

	if (icons.length === 0 && !reserveMaximumWidth) {
		return null;
	}

	return (
		<span
			css={[styles.stack, reserveMaximumWidth && styles.reservedStack]}
			aria-hidden="true"
			data-testid="byline-connections-icon-stack"
		>
			{icons.map((icon, index) => {
				switch (index) {
					case 0:
						return (
							<span
								key={icon.key}
								css={[
									iconTileStyles,
									appearance === 'placeholder' && placeholderIconTileStyles,
									firstIconTileStyles,
								]}
							>
								<SmartLinkIconRenderer icon={icon.icon} size="stack" />
							</span>
						);
					case 1:
						return (
							<span
								key={icon.key}
								css={[
									iconTileStyles,
									appearance === 'placeholder' && placeholderIconTileStyles,
									secondIconTileStyles,
								]}
								data-rotation-enabled
							>
								<SmartLinkIconRenderer icon={icon.icon} size="stack" />
							</span>
						);
					case 2:
						return (
							<span
								key={icon.key}
								css={[
									iconTileStyles,
									appearance === 'placeholder' && placeholderIconTileStyles,
									thirdIconTileStyles,
								]}
							>
								<SmartLinkIconRenderer icon={icon.icon} size="stack" />
							</span>
						);
					default:
						return null;
				}
			})}
		</span>
	);
};
